/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { KpiCardGrid } from "../kpi_card_grid";
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SmsFailedOutlinedIcon from '@mui/icons-material/SmsFailedOutlined';
import AdsClickOutlinedIcon from '@mui/icons-material/AdsClickOutlined';
import SyncIcon from '@mui/icons-material/Sync';

const GRID_COLUMNS = {
  xs: 1,
  sm: 2,
  md: 2,
  lg: 3,
};

const BORDER_COLOR = "#eee";
const SELECTED_BORDER_COLOR = "#ff653a";
const HOVER_BORDER_COLOR = "#d7e3ff";

const ANALYTICS_CONFIG = [
  {
    key: "total_runs",
    titleKey: "Total Runs",
    descriptionKey: "The total number of background jobs executed",
    icon: <SyncIcon sx={{ color: '#EA580C' }}/>,
    iconAlt: "Total Runs",
    card_value: "total_runs"
  },
  {
    key: "successful_runs",
    titleKey: "Successful Runs",
    descriptionKey: "The total number of background jobs which were successful during the execution",
    icon: <CheckCircleOutlinedIcon sx={{ color: '#EA580C' }}/>,
    iconAlt: "Successful Runs",
    card_value: 'successful_runs'
  },
  {
    key: "failed_runs",
    titleKey: "Failed Runs",
    descriptionKey: "The total number of background jobs which got failed during the execution",
    icon: <SmsFailedOutlinedIcon sx={{ color: '#EA580C' }}/>,
    iconAlt: "Failed Runs",
    card_value: 'failed_runs'
  },
  {
    key: "automatic_runs",
    titleKey: "Automatic Runs",
    descriptionKey: "The total number of background jobs which were executed through sidekiq",
    icon: <AutoModeIcon sx={{ color: '#EA580C' }}/>,
    iconAlt: "Automatic Runs",
    card_value: 'automatic_runs'
  },
  {
    key: "manual_runs",
    titleKey: "Manual Runs",
    descriptionKey: "The total number of background jobs which were executed manually",
    icon: <AdsClickOutlinedIcon sx={{ color: '#EA580C' }}/>,
    iconAlt: "Manual Runs",
    card_value: 'manual_runs'
  },
  {
    key: "records_imported",
    titleKey: "Records Imported",
    descriptionKey: "The total count of records imported from the background jobs",
    icon: <ImportExportIcon sx={{ color: '#EA580C' }}/>,
    iconAlt: "Records Imported",
    card_value: 'records_imported'
  },
];

export const AuditLogAnalyticsCards = ({ 
  isMobile, 
  data = {}, 
  loading = false,
  CLICKABLE_CARDS,
  selectedCard,
  handleCardClick
 }) => {
  const { t } = useTranslation();

  const analyticsConfig = React.useMemo(
    () =>
      ANALYTICS_CONFIG.map((item) => ({
        ...item,
        title: t(item.titleKey),
        description: t(item.descriptionKey),
        icon: item.icon,
        cardTestId: `audit-log-analytics-card-${item.key}`,
        titleTestId: `audit-log-analytics-title-${item.key}`,
        tooltipTestId: `audit-log-analytics-tooltip-${item.key}`,
        valueTestId: `audit-log-analytics-value-${item.key}`,
      })),
    [t]
  );

  const getCardProps = React.useCallback(
    (item) => {
      const isClickable = CLICKABLE_CARDS?.includes(item.card_value);
      const isSelected = isClickable && selectedCard === item.card_value;

      return {
        onClick: isClickable ? () => handleCardClick(item.card_value) : undefined,
        onKeyDown: isClickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleCardClick(item.card_value);
              }
            }
          : undefined,
        role: isClickable ? "button" : undefined,
        tabIndex: isClickable ? 0 : undefined,
        "aria-pressed": isClickable ? isSelected : undefined,
        sx: {
          backgroundColor: isSelected ? "#fff8f6" : "white",
          boxShadow: isSelected ? `inset 0 0 0 1px ${SELECTED_BORDER_COLOR}` : "none",
          transition: "background-color 0.15s ease",
          cursor: isClickable ? "pointer" : "default",
          "&:hover": isClickable && !isSelected
            ? {
                backgroundColor: "#fafcff",
                boxShadow: `inset 0 0 0 1px ${HOVER_BORDER_COLOR}`,
              }
            : undefined,
        },
      };
    },
    [CLICKABLE_CARDS, handleCardClick, selectedCard]
  );

  return (
    <KpiCardGrid
      config={analyticsConfig}
      data={data}
      loading={loading}
      isMobile={isMobile}
      columns={GRID_COLUMNS}
      borderColor={BORDER_COLOR}
      containerTestId="audit-log-analytics-container"
      containerSx={{
        marginTop: "0px",
        marginBottom: "20px",
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: "10px",
      }}
      getCardProps={getCardProps}
    />
  );
};
